import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'

import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useResponsive from '@/components/hooks/useResponsive'
import i18next, { t } from 'i18next'

import { defaultJob } from '@/api/jobs'
import { defaultSection, sectionSchema, SectionType } from '@/api/sections'
import { organizationSectionsQueryOptions, sectionsAvailableQueryOptions, useCreateSectionMutation, useDeleteSectionMutation, useUpdateSectionMutation } from '@/api/sectionsQueryOptions'
import CreateDetailAccordion, { useCreateDetail } from '@/components/detail/CreateDetailRecord'
import AbbreviationColumn from '@/components/grid/AbbreviationColumn'
import NameColumn from '@/components/grid/NameColumn'
import useOrganizationDetailView from '@/components/hooks/useOrganizationDetailView'
import SectionEditCell from '@/components/mui-x/SectionEditCell'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import { getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/sections',
)({
    component: OrganizationComponent,
})

function OrganizationComponent() {
    const params = Route.useParams()

    const sessionOrganization = useSelector(getOrganization);

    const { query, data, createable, updateMutation, deleteMutation, onSubmit } = useOrganizationDetailView<SectionType>(params, organizationSectionsQueryOptions,
        'Section', useCreateSectionMutation, useUpdateSectionMutation, useDeleteSectionMutation)

    const parentSectionsQuery = useSuspenseQuery(sectionsAvailableQueryOptions())
    const parentSections = parentSectionsQuery.data.filter((section: SectionType) => section.organization?.uuid != sessionOrganization.uuid && section.organizationLevel === 'SECTION');


    const basicGridColumns = useBasicGridColumns()

    const { large, xlarge } = useResponsive()

    const columns: GridColDef[] = []

    columns.push(
        AbbreviationColumn(),
        NameColumn(100),
    )

    if (large || xlarge) {

        columns.push(
            {
                field: 'parentSections',
                headerName: i18next.t('property.section.parentSections'),
                renderCell: (params) => {
                    if (params.row.parentSections) {
                        return params.row.parentSections
                            .map((section: SectionType) => section.abbreviation)
                            .join(', ')
                    }
                    return <></>
                },
                renderEditCell: (params: GridRenderCellParams) => {
                    return <SectionEditCell {...params} />
                },
                editable: true,
            }
        )

        columns.push(
            basicGridColumns.startDateColumn,
            basicGridColumns.endDateColumn,
        )
    }
    columns.push(basicGridColumns.descriptionColumn)

    const createDetail = useCreateDetail<SectionType>({
        onSubmit,
        resolver: yupResolver<SectionType, any, any>(sectionSchema),
        defaultValues: {
            ...defaultSection,
            organization: {
                uuid: params.organizationUuid,
            },
        },
        title: t('action.new', { entity: t('entity.section_other') }),
    })

    const editGrid = useEditGrid<SectionType>({
        defaultValues: defaultJob,
        updateMutation: updateMutation,
        deleteMutation: deleteMutation,
        records: data,
        columns: columns,
        title: t('entity.job'),
    })

    if (query.isLoading || query.isFetching) {
        return <LoadProgress />
    }
    if (parentSectionsQuery.isLoading || parentSectionsQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            <Typography>
                {t('entity.section_other')}
            </Typography>
            {createable && (
                <CreateDetailAccordion {...createDetail}>
                    <Grid container spacing={2}>
                        <input
                            type="hidden"
                            defaultValue={params.organizationUuid}
                            {...createDetail.form.register('organization.uuid')}
                        />
                        <input
                            type="hidden"
                            defaultValue={defaultJob.organizationLevel}
                            {...createDetail.form.register('organizationLevel')}
                        />
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={defaultJob.name}
                                required
                                {...createDetail.form.register('name')}
                                label={i18next.t('property.general.name')}
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                helperText={createDetail.form.formState.errors.name?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 4,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={defaultJob.abbreviation}
                                required
                                {...createDetail.form.register('abbreviation')}
                                label={i18next.t('property.general.abbreviation')}
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                helperText={createDetail.form.formState.errors.abbreviation?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregDatePicker
                                name="startDate"
                                defaultValue={dayjs(defaultJob.startDate)}
                                disabled={false}
                                control={createDetail.form.control}
                                label={t('property.general.startDate')}
                            />
                        </Grid>
                        <></>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <Controller
                                control={createDetail.form.control}
                                name='parentSections'
                                defaultValue={[]}
                                render={({ field }) => {
                                    return (
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            getOptionKey={(option) => option.uuid || ''}
                                            getOptionLabel={(option) => option.abbreviation || ''}
                                            filterSelectedOptions
                                            onChange={(_event, item) => {
                                                field.onChange(item)
                                            }}
                                            options={parentSections}
                                            value={field.value}
                                            size="small"
                                            style={{ width: '100%', marginTop: 0 }}
                                            renderInput={(params) => (
                                                <TextField {...params} size="small" label={t('property.section.parentSections')} />
                                            )}
                                        >
                                        </Autocomplete>
                                    );
                                }} />
                        </Grid>
                    </Grid>

                </CreateDetailAccordion>
            )}
            <EditGrid {...editGrid}></EditGrid>
        </>
    )
}
